import { useState, useEffect, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { CustomerSearchCombobox } from "@/components/shared/customer-search-combobox";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { format } from "date-fns";
import {
  User,
  Mail,
  Phone,
  Calendar,
  FileText,
  Activity,
  MessageSquare,
  Save,
  X,
  History,
  Clock,
  ArrowLeft,
  CreditCard,
  FileDown,
  Settings,
  Search,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { useUsersList } from "@/hooks/useUsersData";
import { TransactionTypeBadge } from "@/components/transactions/transaction-type-badge";
import { type Transaction as TransactionFull } from "@/types/transaction";
import { formatCurrency } from "@/lib/utils/formatters";
import { TransactionDetailSheet } from "@/components/transactions/transaction-detail-sheet";
import { ColumnConfigDialog, type ColumnDefinition } from "@/components/shared/column-config-dialog";
import { useScreenColumnPreferences } from "@/hooks/useScreenColumnPreferences";
import { UniqueTableNamesForColumnConfiguration } from "@/config/unique-table-names-for-column-configuration";
import { useStrategiesList } from "@/hooks/useStrategiesData";
import { useTransactionsListGraphQL } from "@/hooks/useTransactionsGraphQL";
import { useCreateCollectionCase, type CreateCollectionCaseRequest } from "@/hooks/useCollectionCasesData";
import { useCreatePromiseContacts, type CreatePromiseFormCustomer } from "@/hooks/useCreatePromiseData";
import type { ContactApi } from "@/types/api/customer-detail";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
  PaginationEllipsis,
} from "@/components/ui/pagination";

interface NewCaseData {
  customerName: string;
  customerId: string;
  customerEmail: string;
  customerPhone: string;
  priority: 'High' | 'Medium' | 'Low';
  status: 'Active' | 'On Hold' | 'Closed' | 'Escalated';
  strategyId: string;
  assignedTo?: string;
  notes: string;
  nextAction: string;
  nextActionDate: string;
}

function getContactPlaceholder(
  selectedCustomer: CreatePromiseFormCustomer | null,
  contactCount: number,
): string {
  if (!selectedCustomer) return 'Select a customer first';
  if (contactCount === 0) return 'No contacts available';
  return 'Select a contact';
}

const DEFAULT_CASE_TRANSACTION_COLUMNS: ColumnDefinition[] = [
  { id: 'referenceNumber', label: 'Reference Number', visible: true, order: 0, locked: true },
  { id: 'transactionDate', label: 'Transaction Date', visible: true, order: 1 },
  { id: 'dueDate', label: 'Due Date', visible: true, order: 2 },
  { id: 'customerName', label: 'Customer Name', visible: false, order: 3 },
  { id: 'transactionType', label: 'Type', visible: true, order: 4 },
  { id: 'currency', label: 'Currency', visible: false, order: 5 },
  { id: 'amount', label: 'Gross', visible: true, order: 6 },
  { id: 'outstandingAmount', label: 'Outstanding', visible: true, order: 7 },
  { id: 'status', label: 'Status', visible: true, order: 8 },
  { id: 'description', label: 'Description', visible: false, order: 9 },
  { id: 'createdBy', label: 'Created By', visible: false, order: 10 },
  { id: 'modifiedBy', label: 'Modified By', visible: false, order: 11 },
  { id: 'accountCode', label: 'Account', visible: false, order: 12 },
  { id: 'netAmount', label: 'Net', visible: false, order: 13 },
  { id: 'vatAmount', label: 'Tax', visible: false, order: 14 },
  { id: 'onDispute', label: 'On Dispute', visible: false, order: 15 },
  { id: 'subLedger', label: 'Sub Ledger', visible: false, order: 16 },
  { id: 'orderNumber', label: 'Order Number', visible: false, order: 17 },
  { id: 'actions', label: 'Actions', visible: false, order: 18 },
];

function formatDisputeValue(onDispute: boolean | null | undefined): string {
  if (onDispute == null) return '';
  return onDispute ? 'Yes' : 'No';
}

function getTransactionStatusBadgeVariant(statusText: string): 'destructive' | 'default' | 'secondary' {
  if (statusText === 'Overdue') return 'destructive';
  if (statusText === 'Completed') return 'default';
  return 'secondary';
}

export default function CreateCasePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const prePopulatedCustomer = location.state?.prePopulatedCustomer;

  const [activeTab, setActiveTab] = useState("overview");
  const [isSaving, setIsSaving] = useState(false);
  const [selectedTransactions, setSelectedTransactions] = useState<Set<string>>(new Set());
  const [transactionSearch, setTransactionSearch] = useState("");
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [txCurrentPage, setTxCurrentPage] = useState(1);
  const [txItemsPerPage, setTxItemsPerPage] = useState(100);
  const [selectedCustomer, setSelectedCustomer] = useState<CreatePromiseFormCustomer | null>(null);
  const [availableContacts, setAvailableContacts] = useState<ContactApi[]>([]);
  const [selectedContact, setSelectedContact] = useState<ContactApi | null>(null);
  const [showCloseConfirmation, setShowCloseConfirmation] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [transactionDetailSheetOpen, setTransactionDetailSheetOpen] = useState(false);
  const [selectedTransactionDetail, setSelectedTransactionDetail] = useState<TransactionFull | null>(null);
  const createCaseMutation = useCreateCollectionCase();

  // Fetch active users for the Assigned To dropdown
  const { data: users = [] } = useUsersList();
  const activeUsers = (users || []).filter(user => user?.status === 'active');
  
  const strategiesQuery = useStrategiesList();
  const availableStrategies = useMemo(() =>
    (strategiesQuery.data ?? []).map(s => ({
      id: s.id,
      name: s.name,
      type: s.type,
    })),
    [strategiesQuery.data]
  );

  // Form data state
  const [formData, setFormData] = useState<NewCaseData>({
    customerName: prePopulatedCustomer?.customerName || "",
    customerId: prePopulatedCustomer?.accountNumber || "",
    customerEmail: "",
    customerPhone: "",
    priority: "Medium",
    status: "Active",
    strategyId: "",
    assignedTo: undefined,
    notes: "",
    nextAction: "Follow-up call",
    nextActionDate: format(new Date(), "yyyy-MM-dd"),
  });

  const selectedCustomerInternalId = selectedCustomer?.id;
  const txQuery = useTransactionsListGraphQL({
    customerId: selectedCustomerInternalId,
    page: txCurrentPage,
    pageSize: txItemsPerPage,
    search: transactionSearch,
    sortBy: sortColumn || undefined,
    sortOrder: sortDirection,
  });
  const availableTransactions: TransactionFull[] = useMemo(() =>
    txQuery.data?.transactions ?? [],
    [txQuery.data]
  );
  const txPagination = useMemo(() => txQuery.data?.pagination ?? { page: 1, pageSize: 100, totalItems: 0, totalPages: 0, hasNextPage: false, hasPreviousPage: false }, [txQuery.data]);

  // Column configuration
  const {
    columns: caseTxCols,
    savePreferences: saveCaseTxPrefs,
    resetToDefaults: resetCaseTxPrefs,
  } = useScreenColumnPreferences(UniqueTableNamesForColumnConfiguration.CreateCaseTransactions, DEFAULT_CASE_TRANSACTION_COLUMNS);
  const columnVisibility = useMemo(() => {
    const vis: Record<string, boolean> = {};
    caseTxCols.forEach((col) => { vis[col.id] = col.visible; });
    return vis;
  }, [caseTxCols]);

  const { contacts: apiContacts } = useCreatePromiseContacts(selectedCustomerInternalId);

  // Track unsaved changes
  useEffect(() => {
    const hasChanges = 
      formData.customerEmail.trim() !== "" ||
      formData.customerPhone.trim() !== "" ||
      formData.notes.trim() !== "" ||
      selectedTransactions.size > 0 ||
      formData.priority !== "Medium" ||
      formData.status !== "Active" ||
      formData.strategyId !== "" ||
      formData.assignedTo !== undefined ||
      formData.nextAction !== "Follow-up call";
    
    setHasUnsavedChanges(prev => prev === hasChanges ? prev : hasChanges);
  }, [formData, selectedTransactions]);

  // Set selectedCustomer from pre-populated customer
  useEffect(() => {
    if (prePopulatedCustomer) {
      setSelectedCustomer({
        id: prePopulatedCustomer.customerId ?? '',
        name: prePopulatedCustomer.customerName ?? '',
        accountNumber: prePopulatedCustomer.accountNumber ?? '',
        email: '',
        phone: '',
      });
      setFormData(prev => ({
        ...prev,
        customerId: prePopulatedCustomer.accountNumber ?? '',
        customerName: prePopulatedCustomer.customerName ?? '',
      }));
    }
  }, [prePopulatedCustomer]);

  // Load contacts when customer is selected or API contacts arrive
  useEffect(() => {
    if (!selectedCustomer) {
      setAvailableContacts([]);
      setSelectedContact(null);
      return;
    }
    if (apiContacts.length === 0) return;

    setAvailableContacts(apiContacts);
    const primaryContact = apiContacts.find(x => x.isPrimary);
    if (primaryContact) {
      setSelectedContact(primaryContact);
      setFormData(prev => ({
        ...prev,
        customerEmail: primaryContact.email,
        customerPhone: primaryContact.phone,
      }));
    }
  }, [selectedCustomer, apiContacts]);

  // Handle customer selection from combobox
  const handleCustomerSelect = (customer: CreatePromiseFormCustomer) => {
    setSelectedCustomer(customer);
    setFormData(prev => ({
      ...prev,
      customerId: customer.accountNumber,
      customerName: customer.name,
      customerEmail: '',
      customerPhone: '',
    }));
    setSelectedTransactions(new Set());
    setSelectedContact(null);
    setAvailableContacts([]);
    setTxCurrentPage(1);
    setHasUnsavedChanges(true);
  };

  const handleBack = () => {
    if (hasUnsavedChanges) {
      setShowCloseConfirmation(true);
      return;
    }
    
    // Check if there's state from navigation (to preserve filters)
    if (location.state?.from === "queue") {
      navigate(-1);
    } else {
      navigate("/collections/queue");
    }
  };

  // Confirm close without saving
  const handleConfirmClose = () => {
    setShowCloseConfirmation(false);
    setHasUnsavedChanges(false);
    
    // Navigate back
    if (location.state?.from === "queue") {
      navigate(-1);
    } else {
      navigate("/collections/queue");
    }
  };

  const handleContactChange = (contactId: string) => {
    const contact = availableContacts.find(c => c.id === contactId);
    if (contact) {
      setSelectedContact(contact);
      setFormData(prev => ({
        ...prev,
        customerEmail: contact.email,
        customerPhone: contact.phone,
      }));
    }
  };

  const handleToggleTransaction = (transactionId: string) => {
    const newSelected = new Set(selectedTransactions);
    if (newSelected.has(transactionId)) {
      newSelected.delete(transactionId);
    } else {
      newSelected.add(transactionId);
    }
    setSelectedTransactions(newSelected);
  };

  const handleViewTransactionDetail = (transaction: TransactionFull) => {
    setSelectedTransactionDetail(transaction);
    setTransactionDetailSheetOpen(true);
  };


  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
    setTxCurrentPage(1);
  };

  // With server-side search/sort/pagination, displayTransactions are already
  // filtered and sorted by the GraphQL hook. Keep local references for the table.
  const sortedTransactions = availableTransactions;

  // Generate page numbers for transaction pagination
  const getTxPageNumbers = () => {
    const pages: (number | string)[] = [];
    const maxVisiblePages = 5;
    const totalPages = txPagination.totalPages;

    if (totalPages <= maxVisiblePages + 2) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);
      if (txCurrentPage > 3) {
        pages.push('ellipsis-start');
      }
      const start = Math.max(2, txCurrentPage - 1);
      const end = Math.min(totalPages - 1, txCurrentPage + 1);
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      if (txCurrentPage < totalPages - 2) {
        pages.push('ellipsis-end');
      }
      pages.push(totalPages);
    }
    return pages;
  };

  const renderSortIcon = (column: string) => {
    if (sortColumn !== column) {
      return <ArrowUpDown className="ml-2 h-4 w-4" />;
    }
    return sortDirection === 'asc' ? (
      <ArrowUp className="ml-2 h-4 w-4" />
    ) : (
      <ArrowDown className="ml-2 h-4 w-4" />
    );
  };

  const handleColumnConfigApply = (columns: ColumnDefinition[]) => {
    saveCaseTxPrefs(columns);
    toast.success("Column configuration updated");
  };

  const handleExportTransactions = (exportAll: boolean) => {
    try {
      if (sortedTransactions.length === 0) {
        toast.error('No transactions to export');
        return;
      }

      const allExportColumns = [
        { key: 'referenceNumber', label: 'Reference Number' },
        { key: 'transactionDate', label: 'Transaction Date' },
        { key: 'dueDate', label: 'Due Date' },
        { key: 'transactionType', label: 'Type' },
        { key: 'amount', label: 'Gross' },
        { key: 'currency', label: 'Currency' },
        { key: 'outstandingAmount', label: 'Outstanding' },
        { key: 'status', label: 'Status' },
        { key: 'description', label: 'Description' },
        { key: 'createdBy', label: 'Created By' },
        { key: 'modifiedBy', label: 'Modified By' },
        { key: 'accountCode', label: 'Account' },
        { key: 'netAmount', label: 'Net' },
        { key: 'vatAmount', label: 'Tax' },
        { key: 'onDispute', label: 'On Dispute' },
        { key: 'subLedger', label: 'Sub Ledger' },
        { key: 'orderNumber', label: 'Order Number' },
        { key: 'customerName', label: 'Customer Name' },
      ];

      const columnsToExport = exportAll
        ? allExportColumns
        : allExportColumns.filter(col => columnVisibility[col.key]);

      const header = columnsToExport.map(col => `"${col.label}"`).join(',');
      const rows = sortedTransactions.map((tx) => {
        const isOverdue = tx.dueDate ? new Date(tx.dueDate) < new Date() : false;
        const isCompleted = (tx.outstandingAmount ?? tx.amount) === 0;
        let statusText = 'Outstanding';
        if (isCompleted) statusText = 'Completed';
        else if (isOverdue) statusText = 'Overdue';

        const valueMap: Record<string, string | number> = {
          referenceNumber: tx.referenceNumber,
          transactionDate: format(new Date(tx.transactionDate), 'dd/MM/yyyy'),
          dueDate: tx.dueDate ? format(new Date(tx.dueDate), 'dd/MM/yyyy') : '',
          transactionType: tx.transactionType ?? '',
          amount: tx.amount,
          currency: tx.currency ?? '',
          outstandingAmount: tx.outstandingAmount ?? tx.amount,
          status: statusText,
          description: tx.description ?? '',
          createdBy: tx.createdBy ?? '',
          modifiedBy: tx.modifiedBy ?? '',
          accountCode: tx.accountCode ?? '',
          netAmount: tx.netAmount ?? '',
          vatAmount: tx.vatAmount ?? '',
          onDispute: formatDisputeValue(tx.onDispute),
          subLedger: tx.subLedger ?? '',
          orderNumber: tx.orderNumber ?? '',
          customerName: tx.customerName ?? formData.customerName,
        };

        return columnsToExport.map(col => {
          const value = valueMap[col.key] ?? '';
          return `"${String(value).replace(/"/g, '""')}"`;
        }).join(',');
      });

      const csv = [header, ...rows].join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `case-transactions-${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.success(`Exported ${sortedTransactions.length} transaction(s) to Excel`);
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export transactions');
    }
  };

  const validateForm = (): boolean => {
    // Required fields validation
    if (!formData.customerId.trim() || !formData.customerName.trim()) {
      toast.error("Please select a customer");
      setActiveTab("overview");
      return false;
    }

    if (!formData.customerEmail.trim()) {
      toast.error("Customer email is required");
      setActiveTab("overview");
      return false;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.customerEmail)) {
      toast.error("Please enter a valid email address");
      setActiveTab("overview");
      return false;
    }

    if (!formData.customerPhone.trim()) {
      toast.error("Customer phone is required");
      setActiveTab("overview");
      return false;
    }

    if (!formData.nextAction.trim()) {
      toast.error("Next action is required");
      setActiveTab("overview");
      return false;
    }

    if (!formData.nextActionDate) {
      toast.error("Next action date is required");
      setActiveTab("overview");
      return false;
    }

    // At least one transaction must be selected
    if (selectedTransactions.size === 0) {
      toast.error("Please select at least one transaction");
      setActiveTab("transactions");
      return false;
    }

    return true;
  };

  const handleSaveCase = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSaving(true);

    try {
      const totalAmount = availableTransactions
        .filter(t => selectedTransactions.has(String(t.id)))
        .reduce((sum, t) => sum + (t.outstandingAmount ?? t.amount), 0);

      const priorityMap: Record<string, number> = { High: 1, Medium: 2, Low: 3 };

      const selectedTransactionIds = Array.from(selectedTransactions).map(Number);

      const request: CreateCollectionCaseRequest = {
        customerId: selectedCustomerInternalId ? parseInt(selectedCustomerInternalId, 10) : 0,
        priorityId: priorityMap[formData.priority] ?? 2,
        strategyId: formData.strategyId ? parseInt(formData.strategyId, 10) : undefined,
        amount: totalAmount,
        nextAction: formData.nextAction || undefined,
        nextActionDate: formData.nextActionDate || undefined,
        contactId: selectedContact ? Number(selectedContact.id) : undefined,
        notes: formData.notes || undefined,
        transactionIds: selectedTransactionIds.length > 0 ? selectedTransactionIds : undefined,
        assignedToUserId: formData.assignedTo ? Number(formData.assignedTo) : undefined,
      };

      await createCaseMutation.mutateAsync(request);

      const transactionCount = selectedTransactionIds.length;
      toast.success(
        transactionCount > 0
          ? `Collection case created successfully with ${transactionCount} transaction(s) linked`
          : "Collection case created successfully"
      );

      setHasUnsavedChanges(false);
      navigate("/collections/queue");
    } catch (error) {
      toast.error("Failed to create case. Please try again.");
      console.error("Error creating case:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const calculateTotalAmount = () => {
    return availableTransactions
      .filter(t => selectedTransactions.has(String(t.id)))
      .reduce((sum, t) => sum + (t.outstandingAmount ?? t.amount), 0);
  };

  return (
    <div className="flex-1 space-y-6 p-6 pt-6">
      {/* Header with Back Button */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={handleBack}
            className="shrink-0"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-3xl font-bold tracking-tight">Create New Case</h1>
            </div>
            <p className="text-muted-foreground mt-1">
              Enter customer and case information to create a new collection case
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="default"
            onClick={handleBack}
            disabled={isSaving}
            className="gap-2"
          >
            <X className="h-4 w-4" />
            Cancel
          </Button>
          <Button
            size="default"
            onClick={handleSaveCase}
            disabled={isSaving}
            className="gap-2"
          >
            <Save className="h-4 w-4" />
            {isSaving ? "Creating..." : "Create Case"}
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="activity">Activity</TabsTrigger>
          <TabsTrigger value="audit">Audit Log</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6">
          {/* Two Column Layout - Customer & Case Info */}
          <div className="grid gap-6 md:grid-cols-[1fr_2fr]">
            {/* Customer Information & Contact */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <User className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                  Customer Information
                </CardTitle>
                <CardDescription>Contact details and customer profile</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                  {/* First Row: Customer and Contact Name */}
                  <div className="grid grid-cols-2 gap-x-4">
                    {/* Customer Dropdown */}
                    <div className="space-y-2">
                      <Label className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                        Customer *
                      </Label>
                      {prePopulatedCustomer ? (
                        <div className="space-y-2">
                          <p className="text-sm font-semibold">{formData.customerName} ({formData.customerId})</p>
                          <Badge variant="secondary" className="text-xs">Pre-selected</Badge>
                        </div>
                      ) : (
                        <CustomerSearchCombobox
                          selectedCustomer={selectedCustomer}
                          onSelectCustomer={handleCustomerSelect}
                        />
                      )}
                    </div>

                    {/* Contact Name Dropdown */}
                    <div className="space-y-2">
                      <Label className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                        <User className="h-3 w-3" />
                        Contact Name
                      </Label>
                      <Select
                        value={selectedContact?.id ?? ''}
                        onValueChange={handleContactChange}
                        disabled={!selectedCustomer || availableContacts.length === 0}
                      >
                        <SelectTrigger className="h-9">
                          <SelectValue placeholder={getContactPlaceholder(selectedCustomer, availableContacts.length)} />
                        </SelectTrigger>
                        <SelectContent>
                          {availableContacts.map((contact) => (
                            <SelectItem key={contact.id} value={contact.id}>
                              {contact.firstName} {contact.surname}
                              {contact.isPrimary && " (Primary)"}
                              {contact.jobTitle && ` - ${contact.jobTitle}`}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Second Row: Email Address and Phone Number */}
                  <div className="grid grid-cols-2 gap-x-4">
                    {/* Email Address (Auto-populated) */}
                    <div className="space-y-2">
                      <Label className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                        <Mail className="h-3 w-3" />
                        Email Address
                      </Label>
                      <Input
                        value={formData.customerEmail}
                        readOnly
                        disabled
                        className="h-9 bg-muted"
                        placeholder="Select a contact to view email"
                      />
                    </div>

                    {/* Phone Number (Auto-populated) */}
                    <div className="space-y-2">
                      <Label className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                        <Phone className="h-3 w-3" />
                        Phone Number
                      </Label>
                      <Input
                        value={formData.customerPhone}
                        readOnly
                        disabled
                        className="h-9 bg-muted"
                        placeholder="Select a contact to view phone"
                      />
                    </div>
                  </div>
              </CardContent>
            </Card>

            {/* Case Management */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                  Case Management
                </CardTitle>
                <CardDescription>Case details and configuration</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* First Row: Case Status, Priority Level, Assigned To */}
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="status" className="text-xs font-medium text-muted-foreground">
                      Case Status
                    </Label>
                    <Select
                      value={formData.status}
                      onValueChange={(value: 'Active' | 'On Hold' | 'Closed' | 'Escalated') => 
                        setFormData(prev => ({ ...prev, status: value }))
                      }
                    >
                      <SelectTrigger id="status" className="h-8">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Active">Active</SelectItem>
                        <SelectItem value="Escalated">Escalated</SelectItem>
                        <SelectItem value="On Hold">On Hold</SelectItem>
                        <SelectItem value="Closed">Closed</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="priority" className="text-xs font-medium text-muted-foreground">
                      Priority Level
                    </Label>
                    <Select
                      value={formData.priority}
                      onValueChange={(value: 'High' | 'Medium' | 'Low') => 
                        setFormData(prev => ({ ...prev, priority: value }))
                      }
                    >
                      <SelectTrigger id="priority" className="h-8">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="High">High</SelectItem>
                        <SelectItem value="Medium">Medium</SelectItem>
                        <SelectItem value="Low">Low</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="assignedTo" className="text-xs font-medium text-muted-foreground">
                      Assigned To
                    </Label>
                    <Select
                      value={formData.assignedTo || ""}
                      onValueChange={(value) => 
                        setFormData(prev => ({ ...prev, assignedTo: value }))
                      }
                    >
                      <SelectTrigger id="assignedTo" className="h-8">
                        <SelectValue placeholder="Select user..." />
                      </SelectTrigger>
                      <SelectContent>
                        {activeUsers.map((user) => (
                          <SelectItem key={user.id} value={user.id}>
                            <div className="flex items-center gap-2">
                              <Avatar className="h-5 w-5">
                                <AvatarImage src={user.avatar} alt={`${user.firstName} ${user.lastName}`} />
                                <AvatarFallback className="text-[10px]">
                                  {user.firstName[0]}{user.lastName[0]}
                                </AvatarFallback>
                              </Avatar>
                              <span>{user.firstName} {user.lastName}</span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <Separator />

                {/* Second Row: Outstanding Amount, Next Action, Due Date */}
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-1">
                    <p className="text-xs font-medium text-muted-foreground">Outstanding Amount</p>
                    <p className="text-base font-bold text-red-600 dark:text-red-400">
                      £{calculateTotalAmount().toLocaleString()}
                    </p>
                    <p className="text-xs text-muted-foreground">From selected transactions</p>
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="nextAction" className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      Next Action <span className="text-red-500">*</span>
                    </Label>
                    <Select
                      value={formData.nextAction}
                      onValueChange={(value) => setFormData(prev => ({ ...prev, nextAction: value }))}
                    >
                      <SelectTrigger id="nextAction" className="h-8">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Follow-up call">Follow-up call</SelectItem>
                        <SelectItem value="Send email">Send email</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="nextActionDate" className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      Due Date <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="nextActionDate"
                      type="date"
                      value={formData.nextActionDate}
                      onChange={(e) => setFormData(prev => ({ ...prev, nextActionDate: e.target.value }))}
                      className="h-8"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Notes Section */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MessageSquare className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
                Case Notes
              </CardTitle>
              <CardDescription>Add notes about this case</CardDescription>
            </CardHeader>
            <CardContent>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Add notes about this case..."
                rows={4}
                className="resize-none"
              />
            </CardContent>
          </Card>

          {/* Transactions Section */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-2 min-w-0">
                  <CreditCard className="h-5 w-5 text-purple-600 dark:text-purple-400 flex-shrink-0" />
                  <div className="min-w-0">
                    <CardTitle className="text-lg">Outstanding Transactions</CardTitle>
                    <CardDescription className="text-xs">
                      {selectedTransactions.size} selected
                    </CardDescription>
                  </div>
                </div>
                {selectedCustomer && (
                <div className="flex items-center gap-2 flex-shrink-0">
                  <div className="relative w-64">
                    <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      placeholder="Search transactions..."
                      value={transactionSearch}
                      onChange={(e) => { setTransactionSearch(e.target.value); setTxCurrentPage(1); }}
                      className="pl-8 h-9"
                    />
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" size="icon" className="h-9 w-9">
                        <FileDown className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => handleExportTransactions(false)}>
                        Export Visible Columns
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleExportTransactions(true)}>
                        Export All Columns
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                  <ColumnConfigDialog
                    columns={caseTxCols}
                    onApply={handleColumnConfigApply}
                    onReset={resetCaseTxPrefs}
                    trigger={
                      <Button variant="outline" size="icon" className="h-9 w-9">
                        <Settings className="h-4 w-4" />
                      </Button>
                    }
                  />
                </div>
                )}
              </div>
              {!selectedCustomer && (
                <CardDescription>
                  Select a customer first to view transactions
                </CardDescription>
              )}
            </CardHeader>
            {selectedCustomer && (
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12"></TableHead>
                    {columnVisibility['referenceNumber'] && (
                      <TableHead className="cursor-pointer select-none hover:bg-muted/50" onClick={() => handleSort('referenceNumber')}>
                        <div className="flex items-center">Reference Number{renderSortIcon('referenceNumber')}</div>
                      </TableHead>
                    )}
                    {columnVisibility['transactionDate'] && (
                      <TableHead className="cursor-pointer select-none hover:bg-muted/50" onClick={() => handleSort('date')}>
                        <div className="flex items-center">Transaction Date{renderSortIcon('date')}</div>
                      </TableHead>
                    )}
                    {columnVisibility['dueDate'] && (
                      <TableHead className="cursor-pointer select-none hover:bg-muted/50" onClick={() => handleSort('dueDate')}>
                        <div className="flex items-center">Due Date{renderSortIcon('dueDate')}</div>
                      </TableHead>
                    )}
                    {columnVisibility['customerName'] && (
                      <TableHead className="cursor-pointer select-none hover:bg-muted/50" onClick={() => handleSort('customerName')}>
                        <div className="flex items-center">Customer Name{renderSortIcon('customerName')}</div>
                      </TableHead>
                    )}
                    {columnVisibility['transactionType'] && (
                      <TableHead className="cursor-pointer select-none hover:bg-muted/50" onClick={() => handleSort('transactionType')}>
                        <div className="flex items-center">Type{renderSortIcon('transactionType')}</div>
                      </TableHead>
                    )}
                    {columnVisibility['currency'] && (
                      <TableHead className="cursor-pointer select-none hover:bg-muted/50" onClick={() => handleSort('currency')}>
                        <div className="flex items-center">Currency{renderSortIcon('currency')}</div>
                      </TableHead>
                    )}
                    {columnVisibility['amount'] && (
                      <TableHead className="text-right cursor-pointer select-none hover:bg-muted/50" onClick={() => handleSort('amount')}>
                        <div className="flex items-center justify-end">Gross{renderSortIcon('amount')}</div>
                      </TableHead>
                    )}
                    {columnVisibility['outstandingAmount'] && (
                      <TableHead className="text-right cursor-pointer select-none hover:bg-muted/50" onClick={() => handleSort('outstanding')}>
                        <div className="flex items-center justify-end">Outstanding{renderSortIcon('outstanding')}</div>
                      </TableHead>
                    )}
                    {columnVisibility['status'] && (
                      <TableHead className="cursor-pointer select-none hover:bg-muted/50" onClick={() => handleSort('status')}>
                        <div className="flex items-center">Status{renderSortIcon('status')}</div>
                      </TableHead>
                    )}
                    {columnVisibility['description'] && (
                      <TableHead className="cursor-pointer select-none hover:bg-muted/50" onClick={() => handleSort('description')}>
                        <div className="flex items-center">Description{renderSortIcon('description')}</div>
                      </TableHead>
                    )}
                    {columnVisibility['createdBy'] && (
                      <TableHead className="cursor-pointer select-none hover:bg-muted/50" onClick={() => handleSort('createdBy')}>
                        <div className="flex items-center">Created By{renderSortIcon('createdBy')}</div>
                      </TableHead>
                    )}
                    {columnVisibility['modifiedBy'] && (
                      <TableHead className="cursor-pointer select-none hover:bg-muted/50" onClick={() => handleSort('modifiedBy')}>
                        <div className="flex items-center">Modified By{renderSortIcon('modifiedBy')}</div>
                      </TableHead>
                    )}
                    {columnVisibility['accountCode'] && (
                      <TableHead className="cursor-pointer select-none hover:bg-muted/50" onClick={() => handleSort('accountCode')}>
                        <div className="flex items-center">Account{renderSortIcon('accountCode')}</div>
                      </TableHead>
                    )}
                    {columnVisibility['netAmount'] && (
                      <TableHead className="text-right cursor-pointer select-none hover:bg-muted/50" onClick={() => handleSort('netAmount')}>
                        <div className="flex items-center justify-end">Net{renderSortIcon('netAmount')}</div>
                      </TableHead>
                    )}
                    {columnVisibility['vatAmount'] && (
                      <TableHead className="text-right cursor-pointer select-none hover:bg-muted/50" onClick={() => handleSort('vatAmount')}>
                        <div className="flex items-center justify-end">Tax{renderSortIcon('vatAmount')}</div>
                      </TableHead>
                    )}
                    {columnVisibility['onDispute'] && (
                      <TableHead className="cursor-pointer select-none hover:bg-muted/50" onClick={() => handleSort('onDispute')}>
                        <div className="flex items-center">On Dispute{renderSortIcon('onDispute')}</div>
                      </TableHead>
                    )}
                    {columnVisibility['subLedger'] && (
                      <TableHead className="cursor-pointer select-none hover:bg-muted/50" onClick={() => handleSort('subLedger')}>
                        <div className="flex items-center">Sub Ledger{renderSortIcon('subLedger')}</div>
                      </TableHead>
                    )}
                    {columnVisibility['orderNumber'] && (
                      <TableHead className="cursor-pointer select-none hover:bg-muted/50" onClick={() => handleSort('orderNumber')}>
                        <div className="flex items-center">Order Number{renderSortIcon('orderNumber')}</div>
                      </TableHead>
                    )}
                    {columnVisibility['actions'] && (
                      <TableHead>Actions</TableHead>
                    )}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedTransactions.map((transaction) => {
                    const txId = String(transaction.id);
                    const isOverdue = transaction.dueDate ? new Date(transaction.dueDate) < new Date() : false;
                    const isCompleted = (transaction.outstandingAmount ?? transaction.amount) === 0;
                    let statusText = 'Outstanding';
                    if (isCompleted) statusText = 'Completed';
                    else if (isOverdue) statusText = 'Overdue';

                    return (
                      <TableRow
                        key={transaction.id}
                        className={cn(
                          "cursor-pointer hover:bg-muted/50",
                          selectedTransactions.has(txId) && "bg-muted/30"
                        )}
                        onClick={() => handleToggleTransaction(txId)}
                      >
                        <TableCell className="w-12" onClick={(e) => e.stopPropagation()}>
                          <Checkbox
                            checked={selectedTransactions.has(txId)}
                            onCheckedChange={() => handleToggleTransaction(txId)}
                            aria-label={`Select ${transaction.referenceNumber}`}
                          />
                        </TableCell>
                        {columnVisibility['referenceNumber'] && (
                          <TableCell className="font-medium">
                            <button
                              onClick={(e) => { e.stopPropagation(); handleViewTransactionDetail(transaction); }}
                              className="text-primary hover:underline transition-colors text-left"
                              title={`View details for ${transaction.referenceNumber}`}
                            >
                              {transaction.referenceNumber}
                            </button>
                          </TableCell>
                        )}
                        {columnVisibility['transactionDate'] && (
                          <TableCell>{format(new Date(transaction.transactionDate), "dd/MM/yyyy")}</TableCell>
                        )}
                        {columnVisibility['dueDate'] && (
                          <TableCell>{transaction.dueDate ? format(new Date(transaction.dueDate), "dd/MM/yyyy") : '-'}</TableCell>
                        )}
                        {columnVisibility['customerName'] && (
                          <TableCell>{transaction.customerName ?? formData.customerName}</TableCell>
                        )}
                        {columnVisibility['transactionType'] && (
                          <TableCell><TransactionTypeBadge type={transaction.transactionType} /></TableCell>
                        )}
                        {columnVisibility['currency'] && (
                          <TableCell>{transaction.currency ?? ''}</TableCell>
                        )}
                        {columnVisibility['amount'] && (
                          <TableCell className="text-right">{formatCurrency(transaction.amount)}</TableCell>
                        )}
                        {columnVisibility['outstandingAmount'] && (
                          <TableCell className="text-right font-medium">{formatCurrency(transaction.outstandingAmount ?? transaction.amount)}</TableCell>
                        )}
                        {columnVisibility['status'] && (
                          <TableCell>
                            <Badge variant={getTransactionStatusBadgeVariant(statusText)}>
                              {statusText}
                            </Badge>
                          </TableCell>
                        )}
                        {columnVisibility['description'] && (
                          <TableCell className="max-w-[200px] truncate">{transaction.description ?? ''}</TableCell>
                        )}
                        {columnVisibility['createdBy'] && (
                          <TableCell>{transaction.createdBy ?? ''}</TableCell>
                        )}
                        {columnVisibility['modifiedBy'] && (
                          <TableCell>{transaction.modifiedBy ?? ''}</TableCell>
                        )}
                        {columnVisibility['accountCode'] && (
                          <TableCell>{transaction.accountCode ?? ''}</TableCell>
                        )}
                        {columnVisibility['netAmount'] && (
                          <TableCell className="text-right">{transaction.netAmount != null ? formatCurrency(transaction.netAmount) : ''}</TableCell>
                        )}
                        {columnVisibility['vatAmount'] && (
                          <TableCell className="text-right">{transaction.vatAmount != null ? formatCurrency(transaction.vatAmount) : ''}</TableCell>
                        )}
                        {columnVisibility['onDispute'] && (
                          <TableCell>{formatDisputeValue(transaction.onDispute)}</TableCell>
                        )}
                        {columnVisibility['subLedger'] && (
                          <TableCell>{transaction.subLedger ?? ''}</TableCell>
                        )}
                        {columnVisibility['orderNumber'] && (
                          <TableCell>{transaction.orderNumber ?? ''}</TableCell>
                        )}
                        {columnVisibility['actions'] && (
                          <TableCell>
                            <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); handleViewTransactionDetail(transaction); }}>
                              View
                            </Button>
                          </TableCell>
                        )}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>

              {/* Pagination Controls */}
              {txPagination.totalItems > 0 && (
                <div className="mt-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                  {/* Items per page selector */}
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">Rows per page:</span>
                    <Select
                      value={txItemsPerPage.toString()}
                      onValueChange={(value) => {
                        setTxItemsPerPage(Number(value));
                        setTxCurrentPage(1);
                      }}
                    >
                      <SelectTrigger className="w-[80px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="25">25</SelectItem>
                        <SelectItem value="50">50</SelectItem>
                        <SelectItem value="100">100</SelectItem>
                      </SelectContent>
                    </Select>
                    <span className="text-sm text-muted-foreground">
                      Showing {((txCurrentPage - 1) * txItemsPerPage) + 1}-{Math.min(txCurrentPage * txItemsPerPage, txPagination.totalItems)} of {txPagination.totalItems} transactions
                    </span>
                  </div>

                  {/* Pagination */}
                  {txPagination.totalPages > 1 && (
                    <Pagination>
                      <PaginationContent>
                        <PaginationItem>
                          <PaginationPrevious
                            onClick={() => setTxCurrentPage((prev) => Math.max(1, prev - 1))}
                            className={cn(
                              txCurrentPage === 1 && "pointer-events-none opacity-50",
                              "cursor-pointer"
                            )}
                          />
                        </PaginationItem>

                        {getTxPageNumbers().map((page, index) => (
                          <PaginationItem key={`${page}-${index}`}>
                            {typeof page === 'number' ? (
                              <PaginationLink
                                onClick={() => setTxCurrentPage(page)}
                                isActive={txCurrentPage === page}
                                className="cursor-pointer"
                              >
                                {page}
                              </PaginationLink>
                            ) : (
                              <PaginationEllipsis />
                            )}
                          </PaginationItem>
                        ))}

                        <PaginationItem>
                          <PaginationNext
                            onClick={() => setTxCurrentPage((prev) => Math.min(txPagination.totalPages, prev + 1))}
                            className={cn(
                              txCurrentPage === txPagination.totalPages && "pointer-events-none opacity-50",
                              "cursor-pointer"
                            )}
                          />
                        </PaginationItem>
                      </PaginationContent>
                    </Pagination>
                  )}
                </div>
              )}

              <Separator className="my-4" />
              <div className="flex justify-between items-center">
                <span className="font-semibold">Total Outstanding:</span>
                <span className="text-xl font-bold text-red-600 dark:text-red-400">
                  {formatCurrency(calculateTotalAmount())}
                </span>
              </div>
            </CardContent>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="activity" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Activity Timeline</CardTitle>
              <CardDescription>
                Activity history will appear here after the case is created
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-center py-12 text-muted-foreground">
                <Activity className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p className="text-sm">No activity yet</p>
                <p className="text-xs mt-1">Activities will be logged once the case is created</p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="audit" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <History className="h-5 w-5" />
                Audit Log
              </CardTitle>
              <CardDescription>
                Audit trail will be available after the case is created
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-center py-12 text-muted-foreground">
                <History className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p className="text-sm">No audit entries yet</p>
                <p className="text-xs mt-1">All case modifications will be logged after creation</p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Confirmation Dialog for Unsaved Changes */}
      <AlertDialog open={showCloseConfirmation} onOpenChange={setShowCloseConfirmation}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Unsaved Changes</AlertDialogTitle>
            <AlertDialogDescription>
              You have unsaved changes. Are you sure you want to close without saving? All your changes will be lost.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Continue Editing</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmClose} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Discard Changes
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Transaction Detail Sheet */}
      <TransactionDetailSheet
        open={transactionDetailSheetOpen}
        onOpenChange={setTransactionDetailSheetOpen}
        transaction={selectedTransactionDetail}
      />
    </div>
  );
}
